import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { geoContains, geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import type { Feature, FeatureCollection, MultiPoint } from "geojson";
import type { GeometryCollection, Topology } from "topojson-specification";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { OverviewSites } from "../Types";
import { ServiceHealth, ServiceScope, sitesInScope } from "./overviewModel";
import {
  LabelBox,
  LabelSide,
  MapNode,
  Rect,
  buildMapClouds,
  buildMapLinks,
  buildMapNodes,
  isPlaced,
  layoutCloudColumn,
  placeLabels,
  placeLinkCaption,
  rectFor,
  shiftIntoView,
} from "./mapModel";
import { CloudMark, HEALTH_FILL, HEALTH_STROKE, PILL_BORDER, TopologyLegend, cloudPlateClass } from "./topologyParts";
import { EntranceProps, introTimeline, prefersReducedMotion, useTopologyIntro } from "./useTopologyIntro";

const WIDTH = 800;
/**
 * The map takes the height the screen allows, not a fixed ratio: it is the
 * page's protagonist. The viewBox follows the box — 800 units wide and as tall
 * as its proportions give — so a taller box frames the scope larger rather than
 * stretching it. Until measured, 800 × 380.
 */
const DEFAULT_HEIGHT = 380;
const MAP_HEIGHT_CLASS = "h-[calc(100vh-13rem)] min-h-[26rem] max-h-[56rem]";
const PADDING = 56;
/** Room kept on the right for the cloud nodes when there are any. */
const CLOUD_GUTTER = 150;
/** Extra room under the cloud column for the last node's label, clear of the map controls. */
const CLOUD_LABEL_ROOM = 36;
/** How far inside the edge, in map units, panning and zooming keep the scope's pins. */
const KEEP_IN_VIEW_MARGIN = 28;
/**
 * The smallest area framed, in degrees. Two sites 200 km apart would otherwise
 * fill the frame edge to edge; this keeps the states around them, which is
 * what tells the reader where they are.
 */
const MIN_SPAN_DEG = 1.5;
/** Pins closer than this, in map units, share one (pins are 26 units wide). */
const PIN_MERGE_DISTANCE = 30;

/** Card size in CSS px, estimated from its text (Quicksand 14 px bold, body 11 px). */
/**
 * The pill's size in CSS px, estimated from its text (Quicksand 12 px bold for
 * the name, 10 px for the ports). Labels are placed for the pill; the full
 * card opened on hover or selection floats over its neighbours instead of
 * shuffling them.
 */
function cardSizePx(node: MapNode): { width: number; height: number } {
  return {
    width: node.shortLabel.length * 7.4 + portsText(node).length * 5.6 + 30,
    height: 26,
  };
}

function portsText(node: MapNode): string {
  return `${node.ports} ${node.ports === 1 ? "port" : "ports"}`;
}

/** Natural Earth names a reader would not recognise, by ISO 3166-2 code. */
const STATE_NAME_OVERRIDES: Record<string, string> = {
  "MX-DIF": "Mexico City",
  "MX-MEX": "State of Mexico",
};

/** Smallest projected area, in map units², a state needs to carry its name. */
const MIN_LABELLED_AREA = 1800;
/** Approximate width of one character of a state name at 9 map units. */
const STATE_CHAR_WIDTH = 5.2;
const STATE_LABEL_HEIGHT = 11;

interface StateLabel {
  key: number;
  name: string;
  x: number;
  y: number;
}

/**
 * Names for the states in view, placed at their centroid, faint enough to read
 * as background. A name is left out when the state is too small to hold it,
 * when it would sit on a pin or under a card, or when it would collide with a
 * name already placed — a crowded map of the Bajío stays legible that way.
 */
function stateLabels(
  states: FeatureCollection,
  path: ReturnType<typeof geoPath>,
  pins: Point[],
  cards: Rect[],
  bounds: { width: number; height: number }
): StateLabel[] {
  const placed: Rect[] = [...cards];
  const labels: StateLabel[] = [];
  states.features
    .map((state, index) => ({ state, index, area: path.area(state) }))
    .sort((a, b) => b.area - a.area)
    .forEach(({ state, index, area }) => {
      const properties = (state.properties ?? {}) as {
        name?: string | null;
        iso_3166_2?: string;
      };
      const name = STATE_NAME_OVERRIDES[properties.iso_3166_2 ?? ""] ?? properties.name;
      if (!name || area < MIN_LABELLED_AREA) return;
      const [x, y] = path.centroid(state);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      const halfWidth = (name.length * STATE_CHAR_WIDTH) / 2;
      const rect = {
        left: x - halfWidth,
        right: x + halfWidth,
        top: y - STATE_LABEL_HEIGHT,
        bottom: y + 2,
      };
      const [[minX], [maxX]] = path.bounds(state);
      const fits = maxX - minX > halfWidth * 2;
      const inView = rect.left > 4 && rect.right < bounds.width - 4 && rect.top > 4 && rect.bottom < bounds.height - 4;
      const clearOfPins = pins.every(([px, py]) => Math.abs(px - x) > halfWidth + 18 || Math.abs(py - y) > 24);
      const clearOfLabels = placed.every(
        (other) =>
          rect.right < other.left || rect.left > other.right || rect.bottom < other.top || rect.top > other.bottom
      );
      if (!fits || !inView || !clearOfPins || !clearOfLabels) return;
      placed.push(rect);
      labels.push({ key: index, name, x, y });
    });
  return labels;
}

const CARD_TRANSFORM: Record<LabelSide, string> = {
  right: "translate(22px, -50%)",
  left: "translate(calc(-100% - 22px), -50%)",
  below: "translate(-50%, 22px)",
  above: "translate(-50%, calc(-100% - 22px))",
};

/**
 * State and province outlines (Natural Earth admin-1, public domain) for the
 * countries Kontrol Edge has sites in, simplified to ~150 KB. Served from
 * `public/` and fetched once, only when a map is drawn.
 */
/** A card pops out of its pin: it grows from the side that faces it. */
const CARD_ORIGIN: Record<LabelSide, string> = {
  right: "origin-left",
  left: "origin-right",
  below: "origin-top",
  above: "origin-bottom",
};

const STATES_URL = "/data/geo/admin1-mx-us.topo.json";
let statesPromise: Promise<FeatureCollection> | null = null;
function loadStates(): Promise<FeatureCollection> {
  statesPromise ??= fetch(STATES_URL)
    .then((response) => {
      if (!response.ok) throw new Error(`${STATES_URL}: ${response.status}`);
      return response.json() as Promise<Topology<{ states: GeometryCollection }>>;
    })
    .then((topology) => feature(topology, topology.objects.states) as FeatureCollection)
    .catch((error) => {
      statesPromise = null;
      throw error;
    });
  return statesPromise;
}

/** The sites' bounding box, grown to at least MIN_SPAN_DEG on each axis. */
function framingArea(nodes: { lat: number; lng: number }[]): Feature<MultiPoint> {
  const lats = nodes.map((node) => node.lat);
  const lngs = nodes.map((node) => node.lng);
  const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
  const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
  const halfLat = Math.max((Math.max(...lats) - Math.min(...lats)) / 2, MIN_SPAN_DEG / 2);
  const halfLng = Math.max((Math.max(...lngs) - Math.min(...lngs)) / 2, MIN_SPAN_DEG / 2);
  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "MultiPoint",
      coordinates: [
        [centerLng - halfLng, centerLat - halfLat],
        [centerLng + halfLng, centerLat + halfLat],
      ],
    },
  };
}

type Point = [number, number];

/** A Mercator view: its scale and where it puts the origin, in map units. */
interface MapView {
  scale: number;
  translate: Point;
}

/** Zoom limits, relative to the fitted view. */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 24;
/** One button press: ×1.8. */
const ZOOM_STEP = 1.8;
/**
 * Zoom per pixel of wheel delta, as an exponent. One mouse-wheel notch (~100 px)
 * zooms ×1.8; a trackpad's small deltas still glide.
 */
const WHEEL_SENSITIVITY = 0.006;
/**
 * A cloud line bows slightly downward. Its nodes are already placed level with
 * the sites they serve, so a strong arc only sends the line off the top edge.
 */
const CLOUD_BEND = 0.08;
/** How long the map takes to glide to a new scope's frame. */
const GLIDE_MS = 550;
const GLIDE_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

/** A curve between two points, bent to one side so parallel links stay apart. */
function curve([x1, y1]: Point, [x2, y2]: Point, bend = 0.2): string {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  return `M${x1},${y1} Q${mx - (y2 - y1) * bend},${my + (x2 - x1) * bend} ${x2},${y2}`;
}

function curveMidpoint([x1, y1]: Point, [x2, y2]: Point, bend = 0.2): Point {
  // Midpoint of a quadratic Bézier: (P0 + 2·C + P2) / 4.
  const cx = (x1 + x2) / 2 - (y2 - y1) * bend;
  const cy = (y1 + y2) / 2 + (x2 - x1) * bend;
  return [(x1 + 2 * cx + x2) / 4, (y1 + 2 * cy + y2) / 4];
}

/**
 * A line that draws itself end to end during the intro — through a mask, so a
 * dashed line keeps its dashes — and fades in when it appears later.
 */
function TopologyLine({
  maskId,
  entrance,
  d,
  className,
  strokeWidth,
  strokeDasharray,
  height,
}: {
  maskId: string;
  entrance: EntranceProps;
  d: string;
  className: string;
  strokeWidth: number;
  strokeDasharray?: string;
  /** The viewBox height, for the mask's extent. */
  height: number;
}) {
  const line = { d, strokeWidth, strokeDasharray, strokeLinecap: "round" as const };
  if (!entrance.isIntro) return <path {...line} className={`${className} ${entrance.className}`} />;
  return (
    <>
      <mask id={maskId} maskUnits="userSpaceOnUse" x={0} y={0} width={WIDTH} height={height}>
        <path
          d={d}
          fill="none"
          stroke="white"
          strokeWidth={8}
          strokeLinecap="round"
          pathLength={1}
          strokeDasharray="1 1"
          className={entrance.className}
          style={entrance.style}
        />
      </mask>
      <path {...line} className={className} mask={`url(#${maskId})`} />
    </>
  );
}

/** Percent position of an SVG point, for the HTML overlays. */
function percentAt([x, y]: Point, height: number): React.CSSProperties {
  return { left: `${(x / WIDTH) * 100}%`, top: `${(y / height) * 100}%` };
}

/** Zoom in, zoom out and back to the fitted view; quiet, in the corner. */
function MapControls({
  onZoomIn,
  onZoomOut,
  onFit,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
}) {
  const button =
    "flex h-7 min-w-7 items-center justify-center px-2 text-xs font-semibold text-semantic-text-secondary hover:bg-semantic-hover-primary hover:text-semantic-text-primary";
  return (
    <div className="absolute bottom-3 right-3 flex divide-x divide-semantic-border-primary overflow-hidden rounded-md border border-semantic-border-primary bg-semantic-bg-primary shadow-sm">
      <button type="button" className={button} onClick={onZoomOut} aria-label="Zoom out">
        −
      </button>
      <button type="button" className={button} onClick={onZoomIn} aria-label="Zoom in">
        +
      </button>
      <button type="button" className={`${button} text-semantic-text-brand`} onClick={onFit}>
        Fit scope
      </button>
    </div>
  );
}

/**
 * The scope's sites on a map of the states they sit in, with a line for each
 * DCI between them and a dashed line to each cloud their services reach
 * (CTX-868, project "Mapa de sitios en el Overview").
 *
 * Everything drawn comes from the overview endpoint for the selected tenant
 * scope; nothing is per-tenant here. Drawn in SVG with the semantic tokens, so
 * it follows the theme and needs no tile server. Sites with no position are
 * not drawn; the panel lists them.
 */
export function SitesMap({
  locations,
  healthBySite,
  cloudHealthBySite,
  scope,
  selectedKey,
  onSelectNode,
}: {
  locations: OverviewSites;
  healthBySite: Record<string, ServiceHealth>;
  /** The cloud services' state per site, for the colour of the lines to a cloud. */
  cloudHealthBySite: Record<string, ServiceHealth>;
  /** The selected chip: the map shows that kind's sites and lines, and reframes. */
  scope: ServiceScope;
  /** The pin whose services the list shows, if any. */
  selectedKey: string | null;
  /** A click on a pin or its card; a second click on the same pin clears it. */
  onSelectNode: (node: MapNode | null) => void;
}) {
  const [states, setStates] = useState<FeatureCollection | null>(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  /** The pin whose full card is open on hover; the selected one stays open too. */
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  // Rendered px per map unit: the cards are CSS-sized, the map is in viewBox units.
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  /** The viewBox height, in map units: the box's proportions times WIDTH. */
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height: boxHeight } = entry.contentRect;
      if (width <= 0) return;
      setScale(width / WIDTH);
      if (boxHeight > 0) setHeight(Math.round((WIDTH * boxHeight) / width));
    });
    observer.observe(element);
    return () => observer.disconnect();
  });

  useEffect(() => {
    let isCurrent = true;
    loadStates()
      .then((loaded) => isCurrent && setStates(loaded))
      .catch(() => isCurrent && setHasLoadError(true));
    return () => {
      isCurrent = false;
    };
  }, []);

  const showLinks = scope === "all" || scope === "datacenterInterconnect";
  const showClouds = scope === "all" || scope === "cloudConnectivity";
  /** The placed sites the selected chip covers. */
  const scopedSites = useMemo(() => {
    const inScope = sitesInScope(
      scope,
      locations.sites.map((site) => site.datacenterId),
      locations.placements ?? {}
    );
    return locations.sites.filter((site) => inScope.has(site.datacenterId)).filter(isPlaced);
  }, [locations, scope]);
  const scopedIds = useMemo(() => new Set(scopedSites.map((site) => site.datacenterId)), [scopedSites]);
  /** Whether the cloud column is drawn, known before pins are grouped: it narrows the frame. */
  const hasCloudColumn = showClouds && (locations.cloudLinks ?? []).some((cloudLink) => scopedIds.has(cloudLink.from));

  /** States holding a site are tinted, like the boceto's highlighted areas. */
  const activeStates = useMemo(() => {
    if (!states) return new Set<number>();
    const active = new Set<number>();
    states.features.forEach((state, index) => {
      if (scopedSites.some((site) => geoContains(state, [site.lng, site.lat]))) active.add(index);
    });
    return active;
  }, [states, scopedSites]);

  /**
   * The view "Fit scope" returns to: the whole of every state holding a site,
   * so the reader sees where they are, plus the sites themselves in case the
   * outlines have not loaded.
   */
  const fitView = useMemo<MapView | null>(() => {
    if (scopedSites.length === 0) return null;
    const right = WIDTH - PADDING - (hasCloudColumn ? CLOUD_GUTTER : 0);
    const framing: FeatureCollection = {
      type: "FeatureCollection",
      features: [...(states?.features.filter((_, index) => activeStates.has(index)) ?? []), framingArea(scopedSites)],
    };
    const fitted = geoMercator().fitExtent(
      [
        [PADDING, PADDING],
        [right, height - PADDING],
      ],
      framing
    );
    return { scale: fitted.scale(), translate: fitted.translate() as Point };
  }, [scopedSites, hasCloudColumn, states, activeStates, height]);

  // Zoom and pan live here; a new scope, tenant or set of sites fits again.
  const [view, setView] = useState<MapView | null>(null);
  const currentView = view ?? fitView;
  const viewRef = useRef<MapView | null>(null);
  /**
   * The scope's sites under a view, as a box in map units, and the room they
   * must stay in (left of the cloud column). Read through a ref by the
   * gesture handlers, which are bound once.
   */
  const keepInViewRef = useRef<(view: MapView, box?: (box: Rect) => Rect) => [number, number]>(() => [0, 0]);
  keepInViewRef.current = (view, adjust = (box) => box) => {
    if (scopedSites.length === 0) return [0, 0];
    const project = geoMercator().scale(view.scale).translate(view.translate);
    const points = scopedSites.map((site) => project([site.lng, site.lat]) as Point);
    const box = adjust({
      left: Math.min(...points.map(([x]) => x)),
      right: Math.max(...points.map(([x]) => x)),
      top: Math.min(...points.map(([, y]) => y)),
      bottom: Math.max(...points.map(([, y]) => y)),
    });
    const area = { left: 0, top: 0, right: WIDTH - (hasCloudColumn ? CLOUD_GUTTER : 0), bottom: height };
    return shiftIntoView(box, area, KEEP_IN_VIEW_MARGIN);
  };
  /** The same view moved, if needed, so the scope's sites stay on screen. */
  const keptInView = (view: MapView): MapView => {
    const [dx, dy] = keepInViewRef.current(view);
    return dx === 0 && dy === 0 ? view : { scale: view.scale, translate: [view.translate[0] + dx, view.translate[1] + dy] };
  };
  viewRef.current = currentView;
  /** The view on screen before a refit, which the next frame glides from. */
  const glideFrom = useRef<MapView | null>(null);
  /** No glide while the intro plays: the first frame lands, it does not travel. */
  const isIntroOverRef = useRef(false);
  useEffect(() => {
    if (isIntroOverRef.current && viewRef.current !== fitView) glideFrom.current = viewRef.current;
    setView(fitView);
  }, [fitView]);

  /** Zooms by `factor` keeping `anchor` (map units) still: the cursor, or the centre. */
  const zoomBy = useCallback(
    (factor: number, anchor: Point = [WIDTH / 2, height / 2]) =>
      setView((previous) => {
        const base = previous ?? fitView;
        if (!base || !fitView) return previous;
        const scaleTo = Math.min(Math.max(base.scale * factor, fitView.scale * MIN_ZOOM), fitView.scale * MAX_ZOOM);
        const applied = scaleTo / base.scale;
        const [ax, ay] = anchor;
        return keptInView({
          scale: scaleTo,
          translate: [ax - (ax - base.translate[0]) * applied, ay - (ay - base.translate[1]) * applied],
        });
      }),
    [fitView, height]
  );

  /*
   * Wheel and drag gestures move and scale the layer already drawn with a CSS
   * transform — no projection, no paths — and commit to the view once, when the
   * gesture pauses. Re-projecting every state outline on each wheel tick is what
   * made zooming lag.
   */
  const layerRef = useRef<HTMLDivElement>(null);
  /** The pending gesture, in container px: scale, then offset. */
  const gesture = useRef({ k: 1, x: 0, y: 0 });
  const commitTimer = useRef<number | undefined>(undefined);
  const glide = useRef<Animation | null>(null);

  /** Holds the live gesture back at the edge, as the committed view will be. */
  const clampGesture = () => {
    const base = viewRef.current;
    if (!base) return;
    const pxPerUnit = (containerRef.current?.getBoundingClientRect().width || WIDTH) / WIDTH;
    const { k, x, y } = gesture.current;
    // Under the layer's CSS transform a point at u sits at u·k + offset/pxPerUnit.
    const [dx, dy] = keepInViewRef.current(base, (box) => ({
      left: box.left * k + x / pxPerUnit,
      right: box.right * k + x / pxPerUnit,
      top: box.top * k + y / pxPerUnit,
      bottom: box.bottom * k + y / pxPerUnit,
    }));
    gesture.current = { k, x: x + dx * pxPerUnit, y: y + dy * pxPerUnit };
  };

  const applyGesture = () => {
    const { k, x, y } = gesture.current;
    if (layerRef.current) layerRef.current.style.transform = `translate(${x}px, ${y}px) scale(${k})`;
  };

  const commitGesture = useCallback(() => {
    window.clearTimeout(commitTimer.current);
    const { k, x, y } = gesture.current;
    if (k === 1 && x === 0 && y === 0) return;
    const pxPerUnit = (containerRef.current?.getBoundingClientRect().width || WIDTH) / WIDTH;
    setView((previous) => {
      const base = previous ?? fitView;
      if (!base) return previous;
      return keptInView({
        scale: base.scale * k,
        translate: [base.translate[0] * k + x / pxPerUnit, base.translate[1] * k + y / pxPerUnit],
      });
    });
  }, [fitView]);

  // The committed view is on screen: drop the transform in the same frame.
  // After a refit, the layer starts where the old view had everything and
  // eases into place (FLIP): one composited animation, no re-projection.
  useLayoutEffect(() => {
    gesture.current = { k: 1, x: 0, y: 0 };
    const layer = layerRef.current;
    if (layer) layer.style.transform = "";
    const from = glideFrom.current;
    glideFrom.current = null;
    if (!layer || !from || !currentView || from === currentView || prefersReducedMotion()) return;
    // A point at x₁ = s₁·r + t₁ in the old view sits at x₂ = s₂·r + t₂ now,
    // so x₁ = x₂ / k + (t₁ − t₂ / k) with k = s₂ / s₁.
    const k = currentView.scale / from.scale;
    const pxPerUnit = (containerRef.current?.getBoundingClientRect().width || WIDTH) / WIDTH;
    const dx = pxPerUnit * (from.translate[0] - currentView.translate[0] / k);
    const dy = pxPerUnit * (from.translate[1] - currentView.translate[1] / k);
    glide.current?.cancel();
    glide.current =
      layer.animate?.([{ transform: `translate(${dx}px, ${dy}px) scale(${1 / k})` }, { transform: "none" }], {
        duration: GLIDE_MS,
        easing: GLIDE_EASING,
      }) ?? null;
  }, [currentView]);
  useEffect(() => () => window.clearTimeout(commitTimer.current), []);

  // Wheel zoom needs a non-passive listener: React's onWheel cannot stop the page scrolling.
  useEffect(() => {
    const element = containerRef.current;
    if (!element || !fitView) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      glide.current?.cancel();
      const base = viewRef.current ?? fitView;
      const rect = element.getBoundingClientRect();
      const anchorX = event.clientX - rect.left;
      const anchorY = event.clientY - rect.top;
      // Proportional to the scroll, so a trackpad glides and a mouse wheel steps.
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      const pending = gesture.current;
      const wanted = base.scale * pending.k * Math.exp(-delta * WHEEL_SENSITIVITY);
      const bounded = Math.min(Math.max(wanted, fitView.scale * MIN_ZOOM), fitView.scale * MAX_ZOOM);
      const factor = bounded / (base.scale * pending.k);
      gesture.current = {
        k: pending.k * factor,
        x: anchorX - (anchorX - pending.x) * factor,
        y: anchorY - (anchorY - pending.y) * factor,
      };
      clampGesture();
      applyGesture();
      window.clearTimeout(commitTimer.current);
      commitTimer.current = window.setTimeout(commitGesture, 160);
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, [fitView, commitGesture, states, hasLoadError]);

  /** Drag to pan. Clicks on pins, cards and controls are left alone. */
  const dragFrom = useRef<{ x: number; y: number } | null>(null);
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as Element).closest("button")) return;
    glide.current?.cancel();
    dragFrom.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const from = dragFrom.current;
    if (!from) return;
    const pending = gesture.current;
    gesture.current = {
      k: pending.k,
      x: pending.x + event.clientX - from.x,
      y: pending.y + event.clientY - from.y,
    };
    dragFrom.current = { x: event.clientX, y: event.clientY };
    clampGesture();
    applyGesture();
  };
  const endDrag = () => {
    if (!dragFrom.current) return;
    dragFrom.current = null;
    commitGesture();
  };

  const projection = useMemo(
    () => (currentView ? geoMercator().scale(currentView.scale).translate(currentView.translate) : null),
    [currentView]
  );
  /**
   * Pins grouped by screen distance: two sites share a pin only while theirs
   * would overlap at this zoom, so zooming in always opens a group. A group
   * pin carries its site count, so it never passes for a single site.
   */
  const nodes = useMemo(
    () =>
      projection
        ? buildMapNodes(scopedSites, healthBySite, locations.links, {
            project: (lng, lat) => projection([lng, lat]) as Point,
            radiusPx: PIN_MERGE_DISTANCE,
          })
        : [],
    [projection, scopedSites, healthBySite, locations.links]
  );
  const links = useMemo(
    () => (showLinks ? buildMapLinks(locations.links, nodes) : []),
    [showLinks, locations.links, nodes]
  );
  const clouds = useMemo(
    () => (showClouds ? buildMapClouds(locations.cloudLinks ?? [], nodes, cloudHealthBySite) : []),
    [showClouds, locations.cloudLinks, nodes, cloudHealthBySite]
  );

  const drawing = useMemo(() => {
    if (!projection || nodes.length === 0) return null;
    const pointByNode = new Map(nodes.map((node) => [node.key, projection([node.lng, node.lat]) as Point]));
    const cloudX = WIDTH - CLOUD_GUTTER / 2;
    const cloudY = layoutCloudColumn(clouds, (node) => pointByNode.get(node)?.[1] ?? height / 2, {
      top: PADDING,
      // The label hangs below the last node, and the zoom controls sit in this corner.
      bottom: height - PADDING - CLOUD_LABEL_ROOM,
      minGap: 72,
    });
    const pointByCloud = new Map(clouds.map((cloud) => [cloud.cloud, [cloudX, cloudY[cloud.cloud]] as Point]));
    return { projection, path: geoPath(projection), pointByNode, pointByCloud };
  }, [projection, nodes, clouds, height]);

  // Pins land left to right.
  const pinOrder = new Map(
    nodes
      .map((node) => ({
        key: node.key,
        x: drawing?.pointByNode.get(node.key)?.[0] ?? 0,
      }))
      .sort((a, b) => a.x - b.x)
      .map((pin, index) => [pin.key, index])
  );
  const timeline = introTimeline(nodes.length);
  const { isOver: isIntroOver, entrance } = useTopologyIntro(
    Boolean(drawing && (states || hasLoadError)),
    timeline.endsAt
  );
  isIntroOverRef.current = isIntroOver;
  const maskPrefix = `topology-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

  if (!drawing) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary text-sm text-semantic-text-secondary ${MAP_HEIGHT_CLASS}`}
      >
        No site of this service type has a location on file.
      </div>
    );
  }
  if (!states && !hasLoadError) {
    return (
      <div className={MAP_HEIGHT_CLASS}>
        <SkeletonBlock h="100%" roundedClass="rounded-lg" ariaLabel="Loading map" />
      </div>
    );
  }

  const point = (key: string) => drawing.pointByNode.get(key) as Point;
  const at = (position: Point) => percentAt(position, height);
  const labelBoxes: LabelBox[] = nodes.map((node) => {
    const [x, y] = point(node.key);
    const size = cardSizePx(node);
    return {
      key: node.key,
      x,
      y,
      width: size.width / scale,
      height: size.height / scale,
    };
  });
  const sides = placeLabels(labelBoxes, {
    width: WIDTH - (clouds.length > 0 ? CLOUD_GUTTER : 0),
    height,
  });
  const cardRects = labelBoxes.map((box) => rectFor(box, sides[box.key] ?? "right"));
  // The DCI captions are text on the map too; state names keep off them.
  // DCI captions: pills placed off the pins and cards (placeLinkCaption).
  const pinRects: Rect[] = nodes.map((node) => {
    const [x, y] = point(node.key);
    return { left: x - 16, right: x + 16, top: y - 16, bottom: y + 16 };
  });
  const captions = links.map((link) => {
    const from = point(link.from);
    const to = point(link.to);
    const mid = curveMidpoint(from, to);
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]) || 1;
    const normal: Point = [-(to[1] - from[1]) / length, (to[0] - from[0]) / length];
    const size = {
      width: (link.label.length * 6.2 + 16) / scale,
      height: 18 / scale,
    };
    const placed = placeLinkCaption(mid, normal, size, [...pinRects, ...cardRects], {
      width: WIDTH - (clouds.length > 0 ? CLOUD_GUTTER : 0),
      height,
    });
    return { link, mid, ...placed, size };
  });
  const linkLabelRects: Rect[] = captions.map(({ x, y, size }) => ({
    left: x - size.width / 2,
    right: x + size.width / 2,
    top: y - size.height / 2,
    bottom: y + size.height / 2,
  }));
  const labelsForStates = states
    ? stateLabels(
        states,
        drawing.path,
        nodes.map((node) => point(node.key)),
        [...cardRects, ...linkLabelRects],
        {
          width: WIDTH - (clouds.length > 0 ? CLOUD_GUTTER : 0),
          height,
        }
      )
    : [];

  return (
    <div
      ref={containerRef}
      className={`relative w-full cursor-grab touch-none select-none overflow-hidden rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary active:cursor-grabbing ${MAP_HEIGHT_CLASS}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div ref={layerRef} className="absolute inset-0 origin-top-left will-change-transform">
        <svg viewBox={`0 0 ${WIDTH} ${height}`} className="absolute inset-0 h-full w-full" aria-hidden>
          {/* Highlighted states go last: a neighbour drawn after them would paint its
            grey border over the shared edge and leave their outline broken. */}
          <g className="motion-safe:animate-topology-in">
            {(states?.features ?? []).map((state, index) =>
              activeStates.has(index) ? null : (
                <path
                  key={index}
                  d={drawing.path(state) ?? undefined}
                  className="fill-semantic-bg-primary stroke-semantic-border-primary"
                  strokeWidth={0.8}
                />
              )
            )}
          </g>
          <g>
            {(states?.features ?? []).map((state, index) =>
              activeStates.has(index) ? (
                <path
                  key={index}
                  d={drawing.path(state) ?? undefined}
                  className="fill-semantic-bg-brand-secondary stroke-semantic-border-brand-secondary motion-safe:animate-topology-in"
                  strokeWidth={1.2}
                  strokeLinejoin="round"
                />
              ) : null
            )}
          </g>

          <g aria-hidden className="motion-safe:animate-topology-in">
            {labelsForStates.map((label) => (
              <text
                key={label.key}
                x={label.x}
                y={label.y}
                textAnchor="middle"
                className="fill-semantic-text-secondary stroke-semantic-bg-secondary text-[9px] font-medium"
                strokeWidth={3}
                paintOrder="stroke"
                opacity={0.75}
              >
                {label.name}
              </text>
            ))}
          </g>

          <g fill="none">
            {clouds.flatMap((cloud) =>
              cloud.from.map(({ node, health }, index) => (
                <TopologyLine
                  key={`${cloud.cloud}-${node}`}
                  height={height}
                  maskId={`${maskPrefix}-cloud-${cloud.cloud}-${node}`.replace(/[^a-zA-Z0-9_-]/g, "")}
                  entrance={entrance(`line:${cloud.cloud}-${node}`, "draw", timeline.linesAt + index * 60)}
                  d={curve(point(node), drawing.pointByCloud.get(cloud.cloud) as Point, CLOUD_BEND)}
                  className={HEALTH_STROKE[health]}
                  strokeWidth={2}
                  strokeDasharray="6 5"
                  strokeLinecap="round"
                />
              ))
            )}
            {links.map((link) => (
              <TopologyLine
                key={link.key}
                height={height}
                maskId={`${maskPrefix}-dci-${link.key}`.replace(/[^a-zA-Z0-9_-]/g, "")}
                entrance={entrance(`line:${link.key}`, "draw", timeline.linesAt)}
                d={curve(point(link.from), point(link.to))}
                className="stroke-semantic-bg-brand-bold"
                strokeWidth={2.5}
                strokeLinecap="round"
              />
            ))}
          </g>

          {/* A caption moved off its line keeps a faint leader back to it. */}
          <g>
            {captions
              .filter(({ offset }) => offset !== 0)
              .map(({ link, mid, x, y }) => {
                const appear = entrance(`leader:${link.key}`, "fade", timeline.labelsAt);
                return (
                <line
                  key={link.key}
                  x1={mid[0]}
                  y1={mid[1]}
                  x2={x}
                  y2={y}
                  className={`stroke-semantic-bg-brand-bold ${appear.className}`}
                  style={appear.style}
                  strokeWidth={1}
                  strokeDasharray="2 2"
                />
                );
              })}
          </g>

          <g>
            {nodes.map((node) => {
              const [x, y] = point(node.key);
              const appear = entrance(`pin:${node.key}`, "pop", timeline.pinAt(pinOrder.get(node.key) ?? 0));
              return (
                // The pop scales the inner group about its own origin: the pin centre.
                <g key={node.key} transform={`translate(${x},${y})`}>
                  <g className={appear.className} style={appear.style}>
                    {selectedKey === node.key && <circle r={20} className={HEALTH_FILL[node.health]} opacity={0.2} />}
                    <circle
                      r={13}
                      className={`fill-semantic-bg-primary ${HEALTH_STROKE[node.health]}`}
                      strokeWidth={4}
                    />
                    <circle r={5} className={HEALTH_FILL[node.health]} />
                    {node.siteCount > 1 && (
                      // A group says it is one: the number of sites it holds.
                      <g transform="translate(11,-11)">
                        <circle
                          r={8}
                          className={`${HEALTH_FILL[node.health]} stroke-semantic-bg-primary`}
                          strokeWidth={2}
                        />
                        <text
                          textAnchor="middle"
                          dy="0.35em"
                          className="fill-semantic-text-inverse-primary text-[9px] font-bold"
                        >
                          {node.siteCount}
                        </text>
                      </g>
                    )}
                  </g>
                </g>
              );
            })}
            {clouds.map((cloud) => {
              const [x, y] = drawing.pointByCloud.get(cloud.cloud) as Point;
              const appear = entrance(`cloud:${cloud.cloud}`, "pop", timeline.cloudsAt);
              return (
                <g key={cloud.cloud} transform={`translate(${x},${y})`}>
                  <circle
                    r={17}
                    className={`${cloudPlateClass(cloud.cloud)} stroke-semantic-bg-brand-bold ${appear.className}`}
                    style={appear.style}
                    strokeWidth={3}
                  />
                </g>
              );
            })}
          </g>
        </svg>

        {captions.map(({ link, x, y }) => {
          const appear = entrance(`caption:${link.key}`, "fade", timeline.labelsAt);
          return (
            <span
              key={link.key}
              className={`pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-semantic-border-brand-secondary bg-semantic-bg-primary px-1.5 py-px text-[10px] font-bold text-semantic-text-brand shadow-sm ${appear.className}`}
              style={{ ...at([x, y]), ...appear.style }}
            >
              {link.label}
            </span>
          );
        })}

        {/* Pins and cards are one target: a click narrows the service list to the site. */}
        {nodes.map((node) => (
          <button
            key={`pin-${node.key}`}
            type="button"
            aria-label={`Show services at ${node.label}`}
            aria-pressed={selectedKey === node.key}
            onClick={() => onSelectNode(selectedKey === node.key ? null : node)}
            className="absolute h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={at(point(node.key))}
          />
        ))}
        {nodes.map((node) => {
          const isOpen = hoveredKey === node.key || selectedKey === node.key;
          const side = sides[node.key] ?? "right";
          // Lands just after its pin; the wrapper animates, the button keeps its placement transform.
          const appear = entrance(`card:${node.key}`, "pop", timeline.pinAt(pinOrder.get(node.key) ?? 0) + 80);
          return (
            <button
              key={node.key}
              type="button"
              onClick={() => onSelectNode(selectedKey === node.key ? null : node)}
              onMouseEnter={() => setHoveredKey(node.key)}
              onMouseLeave={() => setHoveredKey((current) => (current === node.key ? null : current))}
              onFocus={() => setHoveredKey(node.key)}
              onBlur={() => setHoveredKey((current) => (current === node.key ? null : current))}
              aria-label={`${node.label}, ${node.detail}`}
              className={`absolute text-left ${isOpen ? "z-10" : ""}`}
              style={{
                ...at(point(node.key)),
                transform: CARD_TRANSFORM[side],
              }}
            >
              <span className={`block ${CARD_ORIGIN[side]} ${appear.className}`} style={appear.style}>
                {isOpen ? (
                  <span className="block whitespace-nowrap rounded-lg border border-semantic-border-brand-primary bg-semantic-bg-primary px-3 py-1.5 shadow-md">
                    <span className="block font-heading text-sm font-bold leading-tight text-semantic-text-primary">
                      {node.label}
                    </span>
                    <span className="block text-[11px] text-semantic-text-secondary">{node.detail}</span>
                  </span>
                ) : (
                  <span
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border bg-semantic-bg-primary px-2.5 py-1 shadow-sm ${PILL_BORDER[node.health]}`}
                  >
                    <span className="font-heading text-xs font-bold text-semantic-text-primary">{node.shortLabel}</span>
                    <span className="text-[10px] text-semantic-text-secondary">{portsText(node)}</span>
                  </span>
                )}
              </span>
            </button>
          );
        })}

        {clouds.map((cloud) => {
          const position = drawing.pointByCloud.get(cloud.cloud) as Point;
          const icon = entrance(`cloud-icon:${cloud.cloud}`, "fade", timeline.cloudsAt + 120);
          const label = entrance(`cloud-label:${cloud.cloud}`, "fade", timeline.labelsAt);
          return (
            <React.Fragment key={cloud.cloud}>
              <CloudMark
                cloud={cloud.cloud}
                className={`pointer-events-none absolute ${icon.className}`}
                style={{
                  ...at(position),
                  transform: "translate(-50%, -50%)",
                  ...icon.style,
                }}
              />
              {/* On a plate, like the pins' labels: the cloud lines converge right here and would run through bare text. */}
              <span
                className={`pointer-events-none absolute whitespace-nowrap rounded-full border border-semantic-border-primary bg-semantic-bg-primary px-2 py-0.5 text-[11px] font-semibold text-semantic-text-brand shadow-sm ${label.className}`}
                style={{
                  ...at(position),
                  transform: "translate(-50%, 20px)",
                  ...label.style,
                }}
              >
                {cloud.cloud} · {cloud.services} {cloud.services === 1 ? "service" : "services"}
              </span>
            </React.Fragment>
          );
        })}
      </div>
      <TopologyLegend hasClouds={clouds.length > 0} />
      <MapControls
        onZoomIn={() => zoomBy(ZOOM_STEP)}
        onZoomOut={() => zoomBy(1 / ZOOM_STEP)}
        onFit={() => setView(fitView)}
      />
    </div>
  );
}
